import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { boundaryLabelKey } from '@/lib/auditLabels';
import { formatDate as formatDayLabel, formatDateTime } from '@/lib/formatDate';
import { groupMessages } from '@/lib/transcriptGrouping';
import { cn } from '@/lib/utils';
import type { ThreadMessage, TurnAudit } from '@/services/conversationService';

// ---------------------------------------------------------------------------
// Audit-friendly label mapping
//
// Maps raw executed_tools values → owner-friendly i18n keys so the owner
// sees "Captou contato" not "register_lead" (F050 requirement: no raw rule
// names dumped at the owner).
// ---------------------------------------------------------------------------
const TOOL_LABEL_KEY: Record<string, string> = {
  register_lead: 'conversations.audit.tool.register_lead',
  send_link: 'conversations.audit.tool.send_link',
  escalate: 'conversations.audit.tool.escalate',
  schedule_appointment: 'conversations.audit.tool.schedule_appointment',
  answer_faq: 'conversations.audit.tool.answer_faq',
};

function toolLabelKey(tool: string): string {
  return TOOL_LABEL_KEY[tool] ?? `conversations.audit.tool.${tool}`;
}

// ---------------------------------------------------------------------------
// F165 — unsupported (attachment-only) inbound marker
//
// A NULL-text inbound message is a media DM (audio/photo/share/sticker) the
// dispatcher persisted-but-skipped, not a gap in the data. Render a friendly
// marker instead of a blank em-dash bubble so the owner sees the customer
// tried something and can reply manually.
// ---------------------------------------------------------------------------
const UNSUPPORTED_TYPE_LABEL_KEY: Record<string, string> = {
  audio: 'conversations.transcript.unsupported.audio',
  image: 'conversations.transcript.unsupported.image',
  video: 'conversations.transcript.unsupported.video',
  share: 'conversations.transcript.unsupported.share',
};

function unsupportedLabelKey(attachmentType: string | null): string {
  return (
    (attachmentType && UNSUPPORTED_TYPE_LABEL_KEY[attachmentType]) ??
    'conversations.transcript.unsupported.generic'
  );
}

// F203 — a shared post/reel/story renders as a card (kind, caption, link)
// instead of the generic unsupported label. Every other kind keeps the label.
const SHARED_KIND_LABEL_KEY: Record<string, string> = {
  ig_post: 'conversations.thread.shared_post',
  ig_reel: 'conversations.thread.shared_reel',
  ig_story: 'conversations.thread.shared_story',
};

function SharedPostCard({ message }: { message: ThreadMessage }) {
  const { t } = useTranslation();
  const labelKey = SHARED_KIND_LABEL_KEY[message.attachment_type ?? ''];
  return (
    <div className="flex min-w-0 flex-col gap-1.5" data-testid="shared-post-card">
      <span className="text-xs font-medium text-muted-foreground">{t(labelKey)}</span>
      {message.attachment_title ? (
        <p className="line-clamp-3 min-w-0 break-words">{message.attachment_title}</p>
      ) : null}
      {message.attachment_url ? (
        <a
          href={message.attachment_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 min-w-0 items-center gap-1.5 text-xs underline underline-offset-2 transition-colors hover:text-muted-foreground"
        >
          <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="min-w-0 truncate">{t('conversations.thread.shared_open')}</span>
        </a>
      ) : null}
    </div>
  );
}

interface AuditLineProps {
  audit: TurnAudit;
}

/**
 * Per-turn audit affordance: a small muted line under a bot reply showing
 * which tools fired and whether a guardrail acted. Designed to be discreet
 * and owner-friendly — no raw rule names (F050).
 */
function AuditLine({ audit }: AuditLineProps) {
  const { t } = useTranslation();

  const parts: string[] = [];

  for (const tool of audit.executed_tools) {
    parts.push(t(toolLabelKey(tool)));
  }

  if (audit.guardrail_allowed === false) {
    parts.push(t('conversations.audit.guardrail_blocked'));
  }

  if (audit.boundary_category) {
    parts.push(t(boundaryLabelKey(audit.boundary_category)));
  }

  if (parts.length === 0) return null;

  return (
    <p
      className="mt-0.5 text-xs text-muted-foreground"
      data-testid="conversation-audit-line"
      aria-label={t('conversations.audit.aria_label')}
    >
      {parts.join(' · ')}
    </p>
  );
}

interface MessageBubbleProps {
  message: ThreadMessage;
  /** First message of a visual group — inbound groups show an avatar here. */
  isGroupStart: boolean;
  /** Last message of a visual group — timestamp renders only here. */
  isGroupEnd: boolean;
  /** First letter of the contact's handle (or '#'), used for the inbound avatar. */
  avatarFallbackChar: string;
}

// Transcript timestamps use formatDateTime (dd/mm/yyyy hh:mm).
const formatTime = formatDateTime;

/**
 * Monochrome message bubble per §design constraints:
 * - Inbound: left-aligned, border + bg-muted (neutral background)
 * - Outbound: right-aligned, border + bg-card (slightly lighter / base)
 * - NO accent colors, NO drop shadows — borders only.
 * - NO preview-chat tokens or ChatBubble reuse.
 *
 * Consecutive same-direction messages within 5 minutes render as a visual
 * group (see `@/lib/transcriptGrouping`): tight spacing, a single trailing
 * timestamp, and — for inbound groups — a shared avatar slot so subsequent
 * bubbles in the group align under the first one's avatar.
 */
function MessageBubble({
  message,
  isGroupStart,
  isGroupEnd,
  avatarFallbackChar,
}: MessageBubbleProps) {
  const { t } = useTranslation();
  const isOwner = message.direction === 'owner';
  const isSharedCard =
    !isOwner &&
    message.unsupported &&
    message.attachment_type !== null &&
    message.attachment_type in SHARED_KIND_LABEL_KEY;
  // Owner (human takeover) bubbles sit on the bot's side of the thread.
  const isOutbound = message.direction === 'outbound' || isOwner;

  const bubble = (
    <div className={cn('flex flex-col', isOutbound ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[75%] whitespace-pre-wrap break-words rounded-2xl border px-3.5 py-2.5 text-sm leading-snug',
          isOutbound ? 'rounded-br-md bg-card' : 'rounded-bl-md bg-muted'
        )}
      >
        {isSharedCard ? (
          <SharedPostCard message={message} />
        ) : message.unsupported ? (
          <span className="italic text-muted-foreground">
            {t(
              isOwner
                ? 'conversations.transcript.owner_shared'
                : unsupportedLabelKey(message.attachment_type)
            )}
          </span>
        ) : (
          (message.text ?? <span className="italic text-muted-foreground">—</span>)
        )}
      </div>
      {isGroupEnd ? (
        <span className="mt-0.5 text-xs text-muted-foreground" data-testid="message-timestamp">
          {formatTime(message.timestamp)}
        </span>
      ) : null}
      {/* F202 FR4 — keyed ONLY off blocked_by_subscription. Cap-hit rows share
          status 'blocked_by_billing' but the subscription is active there. */}
      {message.blocked_by_subscription === true ? (
        <span
          className="mt-0.5 text-xs text-muted-foreground"
          data-testid="blocked-billing-caption"
        >
          {t('conversations.thread.blocked_billing')}
        </span>
      ) : null}
      {isOwner ? (
        <span className="mt-0.5 text-xs text-muted-foreground">
          {t('conversations.thread.owner')}
        </span>
      ) : null}
      {message.direction === 'outbound' && message.audit ? (
        <AuditLine audit={message.audit} />
      ) : null}
    </div>
  );

  return (
    <div data-testid={`message-bubble-${message.direction}`}>
      {isOutbound ? (
        bubble
      ) : (
        <div className="flex items-end gap-2">
          {isGroupStart ? (
            <Avatar className="h-6 w-6 shrink-0">
              <AvatarFallback className="text-xs">{avatarFallbackChar}</AvatarFallback>
            </Avatar>
          ) : (
            <div className="h-6 w-6 shrink-0" aria-hidden="true" />
          )}
          {/* min-w-0: without it the flex item floors at the widest unbreakable
              token (URL/id) and the bubble escapes the card at 375px. */}
          <div className="min-w-0 flex-1">{bubble}</div>
        </div>
      )}
    </div>
  );
}

interface DateSeparatorProps {
  iso: string;
}

/** Centered muted divider marking a calendar-day boundary in the transcript. */
function DateSeparator({ iso }: DateSeparatorProps) {
  return (
    <div className="my-4 flex items-center gap-3" data-testid="conversation-date-separator">
      <div className="h-px flex-1 bg-border" aria-hidden="true" />
      <span className="text-xs text-muted-foreground">{formatDayLabel(iso)}</span>
      <div className="h-px flex-1 bg-border" aria-hidden="true" />
    </div>
  );
}

export interface TranscriptViewProps {
  /** Thread messages, already in chronological (render) order. */
  messages: ThreadMessage[];
  /** First letter of the contact's handle (or '#'), used for the inbound avatar. */
  avatarFallbackChar: string;
  /**
   * When set, renders only the last N messages (client-side tail slice) — used
   * by the `LeadDetailPage` conversation card for a last-10 inline preview.
   * `ConversationDetailPage` omits it to render the full thread.
   */
  limit?: number;
}

/**
 * Shared monochrome transcript renderer (F152 — extracted from
 * `ConversationDetailPage` so the lead detail page's inline preview and the
 * full conversation page don't fork the bubble implementation). Purely
 * presentational: no fetching, no page chrome.
 *
 * MUST NOT import `ChatBubble` or reference any `--preview-chat-*` token —
 * those are the wizard's single sanctioned IG-mimic exception (docs/07
 * §3.11). This transcript stays monochrome (border + bg-muted/bg-card only).
 */
export function TranscriptView({ messages, avatarFallbackChar, limit }: TranscriptViewProps) {
  const { t } = useTranslation();

  const visibleMessages =
    typeof limit === 'number' && messages.length > limit ? messages.slice(-limit) : messages;

  if (visibleMessages.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('conversations.detail.no_messages')}</p>;
  }

  return (
    <div
      className="flex flex-col"
      data-testid="conversation-transcript"
      role="log"
      aria-label={t('conversations.detail.transcript_title')}
    >
      {groupMessages(visibleMessages).map((msg, idx) => {
        const key =
          msg.outbound_message_id ?? msg.inbound_message_id ?? `msg-${msg.timestamp}-${idx}`;
        const spacingClass = msg.showDateSeparator
          ? ''
          : idx === 0
            ? ''
            : msg.isGroupStart
              ? 'mt-4'
              : 'mt-1';
        return (
          <div key={key}>
            {msg.showDateSeparator ? <DateSeparator iso={msg.timestamp} /> : null}
            <div className={spacingClass}>
              <MessageBubble
                message={msg}
                isGroupStart={msg.isGroupStart}
                isGroupEnd={msg.isGroupEnd}
                avatarFallbackChar={avatarFallbackChar}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
